import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Toaster } from '@/components/ui/sonner';
import Home from '@/pages/Home';
import Rezepte from '@/pages/Rezepte';
import RezeptDetail from '@/pages/RezeptDetail';
import Wissen from '@/pages/Wissen';
import WissenArtikel from '@/pages/WissenArtikel';
import Felix from '@/pages/Felix';
import Magazin from '@/pages/Magazin';
import MagazinArtikel from '@/pages/MagazinArtikel';
import Kontakt from '@/pages/Kontakt';
import Impressum from '@/pages/Impressum';
import Datenschutz from '@/pages/Datenschutz';
import Barrierefreiheit from '@/pages/Barrierefreiheit';
import Sitemap from '@/pages/Sitemap';
import NotFound from '@/pages/NotFound';
import AdminLogin from '@/admin/AdminLogin';
import { AdminGuard } from '@/admin/AdminGuard';
import Dashboard from '@/admin/Dashboard';
import RecipesList from '@/admin/RecipesList';
import RecipeEditor from '@/admin/RecipeEditor';
import MediaLibrary from '@/admin/MediaLibrary';
import { PagesList, PageEditor } from '@/admin/Pages';
import Stats from '@/admin/Stats';
import Settings from '@/admin/Settings';

function ScrollAndFocusManager() {
  const { pathname } = useLocation();
  const mainRef = useRef(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    mainRef.current?.focus();
    if (window.fgTrack) window.fgTrack(pathname);
  }, [pathname]);
  return mainRef;
}

function Shell() {
  const mainRef = ScrollAndFocusManager();
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');

  if (isAdmin) {
    return (
      <>
        <Routes>
          <Route path="/admin" element={<AdminLogin />} />
          <Route element={<AdminGuard />}>
            <Route path="/admin/dashboard" element={<Dashboard />} />
            <Route path="/admin/rezepte" element={<RecipesList />} />
            <Route path="/admin/rezepte/neu" element={<RecipeEditor />} />
            <Route path="/admin/rezepte/:id" element={<RecipeEditor />} />
            <Route path="/admin/medien" element={<MediaLibrary />} />
            <Route path="/admin/seiten" element={<PagesList />} />
            <Route path="/admin/seiten/:key" element={<PageEditor />} />
            <Route path="/admin/statistik" element={<Stats />} />
            <Route path="/admin/einstellungen" element={<Settings />} />
          </Route>
        </Routes>
        <Toaster richColors position="bottom-right" />
      </>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-brand-cream">
      <a href="#main-content" className="skip-link" data-testid="skip-to-content">
        Zum Hauptinhalt springen
      </a>
      <Header />
      <main id="main-content" ref={mainRef} tabIndex={-1} className="flex-1 outline-none">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/rezepte" element={<Rezepte />} />
          <Route path="/rezepte/:slug" element={<RezeptDetail />} />
          <Route path="/wissen" element={<Wissen />} />
          <Route path="/wissen/:slug" element={<WissenArtikel />} />
          <Route path="/felix" element={<Felix />} />
          <Route path="/magazin" element={<Magazin />} />
          <Route path="/magazin/:slug" element={<MagazinArtikel />} />
          <Route path="/kontakt" element={<Kontakt />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/barrierefreiheit" element={<Barrierefreiheit />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <Toaster richColors position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
